import { useState } from 'react'
import type { Job } from '@workflow/contracts'
import { Button, DatePicker, Dialog, Dropdown, Field, IconButton, Icons } from '@workflow/ui'
import { errorSentence } from '@/lib/api/client'
import { useScheduleJob, useSession, useSetJobStatus, useUnscheduleJob } from '@/lib/api/hooks'

const pad = (value: number) => String(value).padStart(2, '0')
const dayOf = (date: Date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
const TIMES = Array.from({ length: 48 }, (_, index) => {
  const hours = Math.floor(index / 2)
  const minutes = index % 2 === 0 ? '00' : '30'
  const label = new Date(2000, 0, 1, hours, Number(minutes)).toLocaleTimeString('en', { hour: 'numeric', minute: '2-digit' })
  return { value: `${pad(hours)}:${minutes}`, label }
})

const momentText = new Intl.DateTimeFormat('en', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
export const formatMoment = (iso: string): string => momentText.format(new Date(iso))

/** The next half hour from now, a day ahead — a sensible first answer. */
function firstGuess(current: string | null): { day: string; time: string } {
  const at = current ? new Date(current) : new Date(Date.now() + 86_400_000)
  if (!current) at.setMinutes(at.getMinutes() < 30 ? 30 : 60, 0, 0)
  return { day: dayOf(at), time: `${pad(at.getHours())}:${at.getMinutes() < 30 ? '00' : '30'}` }
}

/**
 * The draft's own line on its page: what a draft is, and — for an
 * administrator — the two ways out of it: publish now, or set the moment it
 * publishes itself, the way a video is scheduled to go live. A scheduled
 * draft says when, and can be moved or taken off the clock.
 */
export function DraftBanner({ job }: { job: Job }) {
  const isAdmin = useSession().data?.account?.role === 'admin'
  const publish = useSetJobStatus()
  const unschedule = useUnscheduleJob()
  const [picking, setPicking] = useState(false)
  const scheduled = job.scheduledPublishAt

  return (
    <div className="mb-5 flex flex-col gap-3 rounded-2xl bg-tint px-5 py-4 sm:flex-row sm:items-center">
      <p className="min-w-0 flex-1 text-ui text-foreground">
        {scheduled ? (
          <>
            <strong className="font-medium">Scheduled.</strong> Publishes {formatMoment(scheduled)} — players hear of it then.
          </>
        ) : (
          <>
            <strong className="font-medium">Private Draft.</strong> Players cannot see it and no notices go out until it is published.
          </>
        )}
      </p>
      {isAdmin && job.archivedAt === null ? (
        <div className="flex shrink-0 gap-2">
          {scheduled ? (
            <Button size="sm" variant="ghost" busy={unschedule.isPending} onClick={() => unschedule.mutate({ id: job.id })}>
              Cancel Schedule
            </Button>
          ) : null}
          <Button size="sm" variant="secondary" icon={<Icons.calendar size="sm" />} onClick={() => setPicking(true)}>
            {scheduled ? 'Change' : 'Schedule'}
          </Button>
          <Button size="sm" variant="primary" busy={publish.isPending} onClick={() => publish.mutate({ id: job.id, status: 'published' })}>
            Publish Now
          </Button>
        </div>
      ) : null}
      <ScheduleDialog job={job} open={picking} onClose={() => setPicking(false)} />
    </div>
  )
}

function ScheduleDialog({ job, open, onClose }: { job: Job; open: boolean; onClose: () => void }) {
  return (
    <Dialog open={open} onClose={onClose} labelledBy="schedule-title" className="sm:max-w-md">
      {open ? <ScheduleBody job={job} onClose={onClose} /> : null}
    </Dialog>
  )
}

function ScheduleBody({ job, onClose }: { job: Job; onClose: () => void }) {
  const schedule = useScheduleJob()
  const [guess] = useState(() => firstGuess(job.scheduledPublishAt))
  const [day, setDay] = useState(guess.day)
  const [time, setTime] = useState(guess.time)
  const at = day ? new Date(`${day}T${time}:00`) : null
  const past = at !== null && at.getTime() <= Date.now()

  return (
    <div className="rounded-3xl border bg-surface shadow-modal">
      <div className="flex items-center justify-between gap-3 px-6 pt-5 sm:px-7">
        <h2 id="schedule-title" className="wf-heading text-lg text-foreground">Schedule Publish</h2>
        <IconButton label="Close" size="sm" onClick={onClose} tooltipSide="bottom"><Icons.close /></IconButton>
      </div>
      <div className="px-6 pt-2 pb-5 sm:px-7">
        <p className="text-caption text-muted-foreground">
          {job.title} stays a private draft until then. At that moment it publishes on its own and the crew already on it is told.
        </p>
        <div className="mt-5 grid grid-cols-[minmax(0,1fr)_minmax(0,8.5rem)] gap-3">
          <Field label="Day" htmlFor="schedule-day">
            <DatePicker id="schedule-day" value={day} onChange={setDay} invalid={past} />
          </Field>
          <Field label="Time" htmlFor="schedule-time">
            <Dropdown id="schedule-time" ariaLabel="Time" value={time} options={TIMES} onChange={setTime} />
          </Field>
        </div>
        {past ? <p className="mt-3 text-caption text-danger">Pick a moment that has not passed yet.</p> : null}
        {schedule.error ? <p className="mt-3 text-caption text-danger">{errorSentence(schedule.error)}</p> : null}
      </div>
      <div className="flex gap-2 border-t border-hairline px-6 py-4 sm:justify-end sm:px-7">
        <Button variant="secondary" onClick={onClose} className="flex-1 sm:flex-none">Cancel</Button>
        <Button
          variant="primary"
          busy={schedule.isPending}
          disabled={!at || past}
          onClick={() => at && schedule.mutate({ id: job.id, at: at.toISOString() }, { onSuccess: onClose })}
          className="flex-1 sm:flex-none"
        >
          Schedule
        </Button>
      </div>
    </div>
  )
}
